/** IXD 편집기 - 에디터 콘텐츠 뷰 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AppPageShell } from "@/components/app-shell";
import { toReportPeriod } from "@/components/common/ixd-report-name";
import type { ReportType } from "@/components/common/report-chip";
import {
  DocumentSidebar,
  useDocumentSidebarToggle,
  type DocumentSidebarCategory,
  type DocumentSidebarSection,
} from "@/components/document-sidebar";
import {
  CompareTableRows,
  findTopmostChangeLocation,
  useGetModifiedCellValues,
} from "@/components/ixd-workbook-match";
import { SidebarInset, SidebarProvider } from "@/styles/ui/sidebar";
import { Button } from "@/toggle-ui-kit/button";
import queries from "@/queries";
import { useIxdEditorStore } from "./store";
import { resolveInitialFsType } from "./resolve-initial-fs-type";
import {
  useIxdEditSession,
  useUnsavedBackGuard,
  useUnsavedChangesGuard,
} from "@/hooks/ixd-edit-session";
import { guardUnsavedExit } from "@/components/modals/unsaved-exit-confirm-modal";
import type { FsType, IXDPreviewResponse } from "@/apis/ixd-editor/types";
import type { DsdFile } from "@/types/dsd";
import type { CellIdentityLocation } from "@/utils/ixd-cell-model";
import { isFinancialStatementChunkTitle } from "@/utils/ixd-editor/chunk-kind";
import { logIxdEditorAction } from "@/utils/usage-log";
import { DownloadCompletePanel } from "./download-complete-panel";
import { EditorHeader, EditorSubHeader } from "./editor-header";
import { useWorkbookCompare } from "./use-workbook-compare";
import { useCompareScrollSpy } from "./use-compare-scroll-spy";
import {
  collapseFromBody,
  INITIAL_COLLAPSE,
  nextCollapse,
  SUB_HEADER_ROW_PX,
} from "./sub-header-collapse";
import { useRejectionGuide } from "./use-rejection-guide";
import { useWorkbookLoadedHint } from "./use-workbook-loaded-hint";
import {
  readSelectedWorkbooks,
  toFsType,
  useUrlWorkbook,
  withSelectedWorkbooks,
} from "./use-url-workbook";

/**
 * 워크북을 고르기 전에도 본문은 같은 컴포넌트가 그린다 — 워크북 쪽 입력만 빈 값이다.
 * (값 반영 버튼·DSD 원본 보기는 워크북 칸 안에 있어 한 칸일 때는 렌더되지 않는다)
 */
type CompareBodyProps = React.ComponentProps<typeof CompareTableRows>;
const NO_MATCHES: CompareBodyProps["matches"] = new Map();
const NO_WORKBOOK_TABLES: CompareBodyProps["workbookOnly"] = [];
const NO_WORKBOOK_TABLES_BY_CHUNK: CompareBodyProps["workbookOnlyByChunkId"] =
  new Map();
const NO_PLAN: CompareBodyProps["plan"] = { status: "error" };
const NO_RECONSTRUCTED_DSD_TABLES: CompareBodyProps["getReconstructedDsdTables"] =
  () => [];
const noop = () => {};

/** URL 의 연결/별도 선택 — 모르는 값은 무시한다(유형 기본값으로 간다) */
const FS_TYPE_PARAM = "fsType";
const parseFsTypeParam = (value: string | null): FsType | null =>
  value === "consolidated" || value === "separate" ? value : null;

interface EditorContentViewProps {
  reportId: string;
  previewData: IXDPreviewResponse;
}

export const EditorContentView = ({
  reportId,
  previewData,
}: EditorContentViewProps) => {
  const selectedFsType = useIxdEditorStore((s) => s.selectedFsType);
  const setSelectedFsType = useIxdEditorStore((s) => s.setSelectedFsType);
  const activeChunkId = useIxdEditorStore((s) => s.activeChunkId);
  const setActiveChunkId = useIxdEditorStore((s) => s.setActiveChunkId);

  // ── 비교할 DSD 워크북 선택 ─────────────────────────────────────────────────
  // 연결·별도를 모두 담은 문서는 유형마다 워크북을 하나씩 고를 수 있다(모달과 같은 규칙).
  const [searchParams, setSearchParams] = useSearchParams();
  // 선택은 URL 에 적어 둔다 — 새로고침·뒤로가기로도 불러온 워크북이 살아남는다.
  const [workbookIdByFsType, setWorkbookIdByFsType] = useState<
    Partial<Record<FsType, string>>
  >(() => readSelectedWorkbooks(searchParams));
  const [showWorkbook, setShowWorkbook] = useState(true);
  const urlWorkbookId = searchParams.get("workbookId");
  // 아래 동기화 effect 가 URL 을 덮어쓰기 전의 값 — 복원은 이걸 봐야 한다.
  const initialUrlFsTypeRef = useRef(
    parseFsTypeParam(searchParams.get(FS_TYPE_PARAM)),
  );

  // URL 로 들어온 워크북(선택 화면이 연 탭·예전 preview 링크)은 유형을 모른 채 시작한다.
  // 워크북을 한 번 읽어 그 유형 칸에 꽂고 화면도 그 유형으로 맞춘다.
  const urlWorkbook = useUrlWorkbook(urlWorkbookId);
  const urlWorkbookFsType = urlWorkbook.fsType;
  const appliedUrlWorkbookIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlWorkbookId || !urlWorkbookFsType) return;
    if (appliedUrlWorkbookIdRef.current === urlWorkbookId) return;
    appliedUrlWorkbookIdRef.current = urlWorkbookId;
    setWorkbookIdByFsType((prev) => ({
      ...prev,
      [urlWorkbookFsType]: urlWorkbookId,
    }));
    setSelectedFsType(urlWorkbookFsType);
  }, [setSelectedFsType, urlWorkbookFsType, urlWorkbookId]);

  // 비교 중인 워크북의 진실 공급원 — preview/plan 조회와 본문(합계 자동계산 저장)이
  // 같은 값을 써야 캐시 키가 어긋나지 않는다. URL 은 이 상태를 effect 로 따라올 뿐이다.
  const compareWorkbookId = workbookIdByFsType[selectedFsType] ?? null;

  const compare = useWorkbookCompare({
    reportId,
    workbookId: compareWorkbookId,
    fsType: selectedFsType,
    notifyOpener: !!urlWorkbookId,
  });
  // 비교할 표가 하나도 없으면(워크북 유형에 맞는 IXD 목차가 없음) 비교 본문 대신 안내로 간다 —
  // 목차·편집 세션은 평범한 편집기 chunk 를 그대로 써서 미저장 변경이 버려지지 않게 한다.
  const isComparing =
    compare.isActive && compare.isReady && compare.chunks.length > 0;

  // 상태가 바뀌면 URL 을 따라오게 한다 — 바뀐 게 없으면 history 를 건드리지 않는다.
  useEffect(() => {
    const next = withSelectedWorkbooks(searchParams, workbookIdByFsType);
    if (next) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, workbookIdByFsType]);

  // 이 문서가 실제로 담은 유형. 상단 전환·워크북 고르기·초기 유형이 같은 사실을 쓴다.
  const availableFsTypes = useMemo<FsType[]>(
    () =>
      [
        previewData.consolidatedChunks.length > 0
          ? ("consolidated" as const)
          : null,
        previewData.separateChunks.length > 0 ? ("separate" as const) : null,
      ].filter((fsType): fsType is FsType => fsType != null),
    [previewData.consolidatedChunks.length, previewData.separateChunks.length],
  );

  // 보고 있던 유형을 되살린다 — URL 에 적혀 있으면 그대로, 없으면 복원한 워크북 쪽으로
  // 옮긴다(고를 때와 같은 규칙). 유형 기본값은 연결이라, 별도만 보던 상태로
  // 새로고침하면 빈 화면처럼 보인다.
  const alignedRestoredFsTypeRef = useRef(false);
  useEffect(() => {
    if (alignedRestoredFsTypeRef.current) return;
    alignedRestoredFsTypeRef.current = true;
    const resolved = resolveInitialFsType({
      urlFsType: initialUrlFsTypeRef.current,
      workbookIdByFsType,
      availableFsTypes,
      current: selectedFsType,
    });
    if (resolved !== selectedFsType) setSelectedFsType(resolved);
  }, [availableFsTypes, selectedFsType, setSelectedFsType, workbookIdByFsType]);

  // 유형 선택도 URL 에 적어 둔다 — 새로고침해도 보던 쪽이 그대로 열린다.
  useEffect(() => {
    if (searchParams.get(FS_TYPE_PARAM) === selectedFsType) return;
    const next = new URLSearchParams(searchParams);
    next.set(FS_TYPE_PARAM, selectedFsType);
    setSearchParams(next, { replace: true });
  }, [searchParams, selectedFsType, setSearchParams]);

  // URL 의 workbookId 는 "이 탭을 열 때 비교하려던 워크북"이다. 그 워크북이 선택에서
  // 빠지면 거짓이 되므로(새로고침하면 되살아난다) 그때만 지운다 — 다른 유형 칸에
  // 남아 있는 워크북의 복원 경로는 건드리지 않는다.
  const clearUrlWorkbookId = useCallback(() => {
    if (!searchParams.has("workbookId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("workbookId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // 비교를 끄고 평범한 값 편집기로 돌아간다.
  const stopCompare = useCallback(() => {
    const stopped = workbookIdByFsType[selectedFsType];
    setWorkbookIdByFsType((prev) => {
      const next = { ...prev };
      delete next[selectedFsType];
      return next;
    });
    // 끈 워크북이 URL 로 들어온 그 워크북이거나, 조회 실패로 어느 칸에도 못 꽂힌
    // 상태면 URL 도 비운다. 다른 유형의 워크북을 끈 것이라면 URL 은 그대로 둔다.
    if (stopped == null || stopped === urlWorkbookId) clearUrlWorkbookId();
  }, [clearUrlWorkbookId, selectedFsType, urlWorkbookId, workbookIdByFsType]);

  const chunks = useMemo(() => {
    if (isComparing) return compare.chunks;
    return selectedFsType === "consolidated"
      ? previewData.consolidatedChunks
      : previewData.separateChunks;
  }, [compare.chunks, isComparing, previewData, selectedFsType]);

  // 목차 클릭 스크롤·scroll spy — 본문이 그리는 chunk 목록만 보면 되므로 비교 여부와 무관하다.
  // "사용자가 직접 스크롤했다"를 아는 곳은 이 훅뿐이다. 저장 안내의 포커스 보정은
  // 아래(useRejectionGuide)에서 만들어지므로 ref 로 한 칸 건너 연결한다.
  const cancelFocusCorrectionRef = useRef<() => void>(() => {});
  const scroll = useCompareScrollSpy(chunks, () =>
    cancelFocusCorrectionRef.current(),
  );

  // 저장 검증의 진실 공급원 — 비교 여부와 무관하게 문서 전체.
  const documentChunks = useMemo(
    () => ({
      consolidated: previewData.consolidatedChunks,
      separate: previewData.separateChunks,
    }),
    [previewData.consolidatedChunks, previewData.separateChunks],
  );

  const session = useIxdEditSession({
    reportId,
    // 거절은 저장 버튼 아래 안내 popover가 셀 위치까지 알려준다 — 토스트 중복 금지.
    showsRejectionGuide: true,
    fsType: selectedFsType,
    consolidatedChunks:
      isComparing && selectedFsType === "consolidated"
        ? compare.chunks
        : previewData.consolidatedChunks,
    separateChunks:
      isComparing && selectedFsType === "separate"
        ? compare.chunks
        : previewData.separateChunks,
    // 비교 본문은 매칭된 표만 그린다 — 저장 검증은 문서 전체를 봐야 워크북을 고르기
    // 전에 한 편집(비교 목록에 없는 표)이 index 미매칭으로 빠지지 않는다.
    documentChunks,
    onSaveSuccess: compare.refetchAfterSave,
  });

  // 재조회 중인 변경은 서버 저장이 이미 끝났으므로 이탈 경고 대상이 아니다.
  // 탭 닫기·새로고침은 브라우저 자체 다이얼로그(커스텀 UI 불가), 뒤로가기는 우리 팝업.
  useUnsavedChangesGuard(session.hasUnguardedChanges);
  useUnsavedBackGuard(session.hasUnguardedChanges, (leave) =>
    guardUnsavedExit(session, leave),
  );

  // 되돌리기·수정 셀 패널 이동 등에서 특정 셀로 스크롤하기 위한 포커스 요청.
  // 같은 위치로 반복 요청해도 effect가 다시 실행되도록 매번 새 객체로 감싼다.
  const [cellFocusRequest, setCellFocusRequest] = useState<{
    location: CellIdentityLocation;
    scrollBehavior?: ScrollBehavior;
  } | null>(null);

  const { identityLocationsByKey, cellIdentityByNormalizedCellId } = session;
  // 수정 셀 선택 시 셀 옆 popover에 기존 값/수정한 값을 표시하기 위한 콜백.
  const getModifiedCellValues = useGetModifiedCellValues({
    chunks,
    fsType: selectedFsType,
    cellChanges: session.cellChanges,
    changeOrigins: session.changeOrigins,
    identityLocationsByKey,
    identityKeyByNormalizedCellId: cellIdentityByNormalizedCellId,
  });

  // 저장 검증이 막은 셀 안내(건수·위치·사유 + 해당 셀로 이동) — 헤더 popover 와
  // 표 안 빨간 표식이 같은 목록을 쓴다.
  const requestCellFocus = useCallback(
    (location: CellIdentityLocation) =>
      setCellFocusRequest({ location, scrollBehavior: "smooth" }),
    [],
  );
  const rejection = useRejectionGuide({
    session,
    fsType: selectedFsType,
    chunks,
    listContainerRef: scroll.listContainerRef,
    onRequestCellFocus: requestCellFocus,
  });
  cancelFocusCorrectionRef.current = rejection.cancelFocusCorrection;

  // 표 안 Ctrl+Z 와 헤더 되돌리기 버튼이 같이 쓰는 하나의 핸들러.
  // undo 스택은 연결/별도를 함께 쓰므로(mixed batch) 되돌린 셀이 보고 있지 않은
  // fsType 에만 있으면 그쪽으로 전환해 변화가 눈에 보이게 한다.
  const undo = session.undo;
  const handleUndo = useCallback(() => {
    const result = undo();
    if (!result || result.changes.length === 0) return;
    toast.success(`${result.changes.length}개 셀의 변경을 되돌렸습니다.`);
    if (!result.changes.some((change) => change.fsType === selectedFsType)) {
      setSelectedFsType(result.changes[0].fsType);
      return;
    }
    // dsd-editor와 동일하게 네이티브 smooth 사용 — 브라우저가 duration을
    // 상한 내에서 조절하므로 이동 거리가 길수록 자연히 빨라진다.
    const location = findTopmostChangeLocation(result.changes, {
      chunks,
      identityLocationsByKey,
      identityKeyByNormalizedCellId: cellIdentityByNormalizedCellId,
    });
    if (location) setCellFocusRequest({ location, scrollBehavior: "smooth" });
  }, [
    cellIdentityByNormalizedCellId,
    chunks,
    identityLocationsByKey,
    selectedFsType,
    setSelectedFsType,
    undo,
  ]);

  // 문서가 담은 재무제표 유형 — 워크북 값 불러오기에서 유형마다 워크북을 고르게 한다
  const reportTypes = useMemo<ReportType[]>(
    () =>
      availableFsTypes.map((fsType) =>
        fsType === "consolidated" ? "CONSOLIDATED" : "SEPARATE",
      ),
    [availableFsTypes],
  );

  // 목차 펼침은 상단 바의 토글이 소유한다 (접힌 동안 hover = 본문 위에 띄움, 클릭 = 고정)
  const sidebar = useDocumentSidebarToggle();

  // ponytail: 미리보기 응답에 파일명·기간이 없어 목록 API에서 찾는다. 백엔드가 preview에 실어주면 교체.
  // 리포트 생성 후 불변 — 포커스 복귀마다 목록을 재조회할 이유가 없어 이 화면에선 고정.
  const { data: reportsData } = useQuery({
    ...queries.ixdEditorReports(),
    staleTime: Infinity,
  });
  const report = reportsData?.reports.find((r) => r.reportId === reportId);
  // preview가 떠 있는데 목록에 reportId가 없으면 계약 위반 — 파일명·칩은 장식이라 화면은 유지하고 보고만 한다.
  useEffect(() => {
    if (reportsData && report === undefined) {
      Sentry.captureMessage(
        `[IXD] 리포트 목록에 현재 reportId 없음 — 헤더 파일명 미표시 (reportId: ${reportId})`,
      );
    }
  }, [reportsData, report, reportId]);

  // 목차: 재무제표 본문 / 주석 두 묶음
  const sidebarCategories = useMemo<DocumentSidebarCategory[]>(() => {
    const statements: DocumentSidebarSection[] = [];
    const notes: DocumentSidebarSection[] = [];
    // chunk title 에는 연결/별도 구분이 없다. 목차는 Figma 처럼 "연결재무상태표"로 보여준다
    const prefix = selectedFsType === "consolidated" ? "연결" : "별도";

    for (const chunk of chunks) {
      const isStatement = isFinancialStatementChunkTitle(chunk.title);
      (isStatement ? statements : notes).push({
        id: chunk.id,
        label: isStatement ? `${prefix}${chunk.title}` : chunk.title,
        // 편집기에는 chunk 단위 변경 데이터가 없다 (cellChanges 키가 chunk를 모른다)
        hasChanges: false,
      });
    }

    const categories: DocumentSidebarCategory[] = [];
    if (statements.length)
      categories.push({ label: "본문", sections: statements });
    if (notes.length) categories.push({ label: "주석", sections: notes });
    return categories;
  }, [chunks, selectedFsType]);

  const sidebarPeriodInfo = useMemo(() => {
    if (!report) return undefined;
    return {
      year: report.periodYear != null ? String(report.periodYear) : "",
      period: toReportPeriod(report.periodType) ?? "",
      // 연결/별도는 리포트가 아니라 현재 토글이 정한다
      reportType: selectedFsType,
    };
  }, [report, selectedFsType]);

  // fsType 전환·비교 진입으로 chunk 목록이 바뀌면 목차 선택을 다시 맞춘다.
  // (비교 chunk 는 필터된 부분집합이라 보고 있던 chunk 가 사라질 수 있다)
  useEffect(() => {
    if (chunks.length === 0) return;
    if (activeChunkId && chunks.some((chunk) => chunk.id === activeChunkId)) {
      return;
    }
    setActiveChunkId(chunks[0].id);
  }, [chunks, activeChunkId, setActiveChunkId]);

  // 본문 스크롤 위치로 현재 목차를 정한다 (scroll spy) — 비교 여부와 무관하다.
  const spyChunkId = scroll.spyChunkId;
  useEffect(() => {
    if (spyChunkId) setActiveChunkId(spyChunkId);
  }, [setActiveChunkId, spyChunkId]);

  const scrollToChunk = scroll.scrollToChunk;

  // 본문을 아래로 내린 만큼 파일 정보 줄이 밀려 올라가 사라지고, 위로 올린 만큼
  // 그 자리에서 다시 드러난다 — 어느 표를 보고 있는지는 표의 sticky 제목이 이미
  // 말해 주므로 그 48px 는 본문에 넘기는 편이 낫다. 계산은 sub-header-collapse.ts.
  const [collapse, setCollapse] = useState(INITIAL_COLLAPSE);
  const showWorkbookLoadedHint = useWorkbookLoadedHint({
    isComparing,
    showWorkbook,
    fsType: selectedFsType,
    rowHidden: collapse.px >= SUB_HEADER_ROW_PX,
  });
  const handleTableScroll = scroll.handleTableScroll;
  const handleBodyScroll = useCallback(
    (container: HTMLDivElement) => {
      handleTableScroll(container);
      setCollapse((prev) => nextCollapse(prev, container));
    },
    [handleTableScroll],
  );
  // 본문이 새로 마운트되거나 아예 사라지면 접힘을 DOM 에서 다시 읽는다. 본문은
  // 유형 전환(아래 `key={selectedFsType}`)마다 remount 되고, 로딩·안내 분기에서는
  // 스크롤 컨테이너 자체가 없다 — 둘 다 scroll 이벤트가 없어 접힘만 남으면 줄이
  // 숨은 채 굳어 `DSD워크북 보기`·`워크북 변경` 진입점을 잃는다(본문이 짧아 스크롤이
  // 불가능한 유형에서는 되돌릴 길도 없다). 계산은 scroll 핸들러와 같은 함수 하나뿐이고,
  // 아래 deps 는 "본문을 새로 그리거나 치우는 값들" — 같은 값이면 React 가 재렌더를 접는다.
  useEffect(() => {
    setCollapse((prev) =>
      collapseFromBody(prev, scroll.listContainerRef.current),
    );
  }, [
    scroll.listContainerRef,
    selectedFsType,
    urlWorkbook.hasError,
    compare.isActive,
    compare.isReady,
    compare.loadError,
    compare.chunks.length,
  ]);

  // 워크북을 불러오면 본문은 매칭된 표만 처음부터 다시 그린다 — 목차 포커스도
  // 본문 맨 위로 돌려놔야 보고 있지 않은 옛 목차가 선택된 채로 남지 않는다.
  const lastIsComparingRef = useRef(isComparing);
  useEffect(() => {
    if (lastIsComparingRef.current === isComparing) return;
    lastIsComparingRef.current = isComparing;
    const first = chunks[0];
    if (isComparing && first) scrollToChunk(first.id);
  }, [chunks, isComparing, scrollToChunk]);

  // 스위치를 끄면 워크북만 있는 행이 접혀 위쪽 높이가 줄어든다 — 보고 있던 목차로
  // 다시 맞춰 스크롤이 엉뚱한 표로 밀리지 않게 한다.
  const lastShowWorkbookRef = useRef(showWorkbook);
  useEffect(() => {
    if (lastShowWorkbookRef.current === showWorkbook) return;
    lastShowWorkbookRef.current = showWorkbook;
    if (isComparing && spyChunkId) scrollToChunk(spyChunkId);
  }, [isComparing, scrollToChunk, showWorkbook, spyChunkId]);

  const handleSectionClick = (section: DocumentSidebarSection) => {
    logIxdEditorAction("목차 이동", { ixdChunkId: section.id });
    setActiveChunkId(section.id);
    scrollToChunk(section.id);
  };

  // 재무제표 본문 칸의 "주석 보러 가기" — 첫 주석 목차로 보낸다. 주석이 하나도
  // 없으면 핸들러를 넘기지 않아 안내에서 버튼 자체가 빠진다.
  const firstNoteChunkId = chunks.find(
    (chunk) => !isFinancialStatementChunkTitle(chunk.title),
  )?.id;
  const handleGoToFirstNote = useCallback(() => {
    if (!firstNoteChunkId) return;
    logIxdEditorAction("목차 이동", { ixdChunkId: firstNoteChunkId });
    setActiveChunkId(firstNoteChunkId);
    scrollToChunk(firstNoteChunkId);
  }, [firstNoteChunkId, scrollToChunk, setActiveChunkId]);

  const handleWorkbooksSelect = (workbooks: DsdFile[]) => {
    const picked: Partial<Record<FsType, string>> = {};
    for (const workbook of workbooks) {
      picked[toFsType(workbook.financialStatementType)] = workbook.id;
    }
    setWorkbookIdByFsType((prev) => ({ ...prev, ...picked }));
    setShowWorkbook(true);
    // 새 선택이 URL 워크북을 밀어냈으면 URL 도 비운다 — 안 비우면 새로고침 때
    // 옛 워크북이 되살아나고, 조회에 실패한 URL 워크북의 에러 안내가 계속 남는다.
    const nextIds = { ...workbookIdByFsType, ...picked };
    if (
      urlWorkbookId != null &&
      !Object.values(nextIds).includes(urlWorkbookId)
    ) {
      clearUrlWorkbookId();
    }
    // 지금 보고 있는 유형에 워크북이 없으면 고른 쪽으로 옮겨 바로 보이게 한다.
    if (!picked[selectedFsType]) {
      const [firstFsType] = Object.keys(picked) as FsType[];
      if (firstFsType) setSelectedFsType(firstFsType);
    }
  };

  const busyLabel = session.downloadInFlight ? "다운로드 중..." : "저장 중...";
  const busyDescription = session.downloadInFlight
    ? "수정된 IXD 파일을 준비하고 있습니다"
    : "수정 내역을 저장하고 있습니다";

  return (
    // isolate: 이 안의 z 값(서브헤더 줄 z-[60], 본문 sticky 제목·busy 오버레이 z-50)을
    // 페이지 안에 가둔다. 안 가두면 body 포털로 뜨는 모달 dim(z-50) 보다 헤더가 위로 올라온다.
    <div className="relative isolate h-full w-full">
      <AppPageShell
        header={
          <EditorHeader
            session={session}
            availableFsTypes={availableFsTypes}
            sidebar={{
              ...sidebar.headerToggle,
              onToggle: () => {
                logIxdEditorAction(
                  sidebar.headerToggle.open ? "목차 접기" : "목차 펼치기",
                );
                sidebar.headerToggle.onToggle();
              },
            }}
            onUndo={handleUndo}
            rejectionGuide={rejection.guide}
          />
        }
      >
        <SidebarProvider
          open={sidebar.open}
          onOpenChange={sidebar.setOpen}
          className="h-full min-h-0"
          style={
            {
              "--sidebar-width": "192px",
              "--sidebar-width-icon": "3rem",
              minHeight: 0,
            } as CSSProperties
          }
        >
          <DocumentSidebar
            variant="flat"
            collapsible="offcanvas"
            showLogoAndToggle={false}
            peek={sidebar.peek}
            periodInfo={sidebarPeriodInfo}
            categories={sidebarCategories}
            activeSection={activeChunkId}
            onSectionClick={handleSectionClick}
          />

          <SidebarInset className="flex min-w-0 flex-col">
            <EditorSubHeader
              filename={report?.originalFilename}
              reportTypes={reportTypes}
              session={session}
              workbookName={isComparing ? compare.workbookName : undefined}
              hasWorkbook={compare.isActive}
              // URL 로 들어온 워크북은 유형 조회가 끝나야 붙는다 — 그 사이에
              // 안내를 띄워 예산을 쓰지 않도록 "오는 중"을 알려준다
              workbookPending={!!urlWorkbookId && !compare.isActive}
              showWorkbook={showWorkbook}
              showWorkbookLoadedHint={showWorkbookLoadedHint}
              collapsedPx={collapse.px}
              onShowWorkbookChange={setShowWorkbook}
              onWorkbooksSelect={handleWorkbooksSelect}
            />
            <div className="flex flex-1 min-w-0 min-h-0 overflow-hidden">
              {urlWorkbook.hasError && !compare.isActive ? (
                // 워크북을 못 읽으면 어느 유형과 비교할지조차 모른다 — 조용히 평범한
                // 편집기를 띄우지 않고 비교를 요청했다는 사실을 화면에 남긴다.
                <CompareNotice message="비교할 워크북을 불러오지 못했습니다.">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={urlWorkbook.retry}
                  >
                    다시 시도
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={stopCompare}
                  >
                    비교 없이 편집
                  </Button>
                </CompareNotice>
              ) : compare.isActive && compare.loadError ? (
                <CompareNotice message="비교 데이터를 불러오지 못했습니다.">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={compare.retry}
                  >
                    다시 시도
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={stopCompare}
                  >
                    비교 없이 편집
                  </Button>
                </CompareNotice>
              ) : compare.isActive && !compare.isReady ? (
                <div className="flex h-full w-full items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-label-alternative" />
                  <span className="body-1-normal-regular text-label-alternative">
                    비교 데이터를 불러오는 중...
                  </span>
                </div>
              ) : compare.isActive && compare.chunks.length === 0 ? (
                // 워크북 유형과 IXD 목차가 맞지 않으면 비교할 표가 0개가 된다 — 빈 화면 대신
                // 이유를 알리고 비교를 끌 길을 준다(URL 에 workbookId 가 남아 새로고침으로는 못 벗어난다).
                <CompareNotice message="이 워크북과 비교할 표가 없습니다.">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={stopCompare}
                  >
                    비교 해제
                  </Button>
                </CompareNotice>
              ) : (
                /* 본문은 비교 여부와 상관없이 같은 컴포넌트가 그린다 (Figma 도 같은
                   카드 컴포넌트를 폭만 바꿔 쓴다). 워크북이 없으면 워크북 쪽 입력만
                   빈 값으로 넘겨 IXD 한 칸짜리 카드가 된다. */
                <CompareTableRows
                  /* 연결/별도는 다른 문서다 — 유형이 바뀌면 본문을 새로 마운트한다.
                     한 인스턴스로 이어 쓰면 두 문서의 chunk id 가 겹치는 행이 가상화
                     상태(마지막 실측 높이)를 물려받아 빈 자리로 남는다. */
                  key={selectedFsType}
                  workbookId={compareWorkbookId ?? ""}
                  reportId={reportId}
                  reportQuarter={isComparing ? compare.reportQuarter : null}
                  chunks={chunks}
                  matches={isComparing ? compare.matches : NO_MATCHES}
                  workbookOnly={
                    isComparing ? compare.workbookOnly : NO_WORKBOOK_TABLES
                  }
                  workbookOnlyByChunkId={
                    isComparing
                      ? compare.workbookOnlyByChunkId
                      : NO_WORKBOOK_TABLES_BY_CHUNK
                  }
                  session={session}
                  scrollContainerRef={scroll.listContainerRef}
                  onScroll={handleBodyScroll}
                  onUndo={handleUndo}
                  focusRequest={cellFocusRequest}
                  rejectedCellNotices={rejection.cellNotices}
                  getModifiedCellValues={getModifiedCellValues}
                  getReconstructedDsdTables={
                    isComparing
                      ? compare.getReconstructedDsdTables
                      : NO_RECONSTRUCTED_DSD_TABLES
                  }
                  isDsdViewsLoading={isComparing && compare.isDsdViewsLoading}
                  hasDsdViewsError={isComparing && compare.hasDsdViewsError}
                  onRequestDsdViews={
                    isComparing ? compare.handleRequestDsdViews : noop
                  }
                  plan={isComparing ? compare.plan : NO_PLAN}
                  showWorkbook={isComparing && showWorkbook}
                  onGoToFirstNote={
                    firstNoteChunkId ? handleGoToFirstNote : undefined
                  }
                />
              )}
            </div>
          </SidebarInset>
        </SidebarProvider>
      </AppPageShell>
      <DownloadCompletePanel
        file={session.downloadedFile}
        reportId={reportId}
        isLocked={session.isActionLocked}
        isRedownloading={session.downloadInFlight}
        onRedownload={() => session.downloadMutation.mutate()}
        onClose={session.clearDownloadedFile}
      />
      {session.isBusy && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-static-black/20 backdrop-blur-[1px] pointer-events-auto cursor-wait"
          aria-busy="true"
        >
          <div className="flex items-center gap-3 rounded-[10px] border border-line-normal bg-background-base px-5 py-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-primary-base" />
            <div className="flex flex-col">
              <span className="label-1-normal-medium text-label-normal">
                {busyLabel}
              </span>
              <span className="caption-2-regular text-label-alternative">
                {busyDescription}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** 비교 본문 자리에 띄우는 안내 — 로드 실패·비교할 표 없음 */
const CompareNotice = ({
  message,
  children,
}: {
  message: string;
  children: ReactNode;
}) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
    <span className="body-1-normal-regular text-label-alternative">
      {message}
    </span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);
